import { ArticleSavingContext } from 'articleEditor/contexts/savingContext';
import { useBaseTranslation } from 'articleEditor/hooks/translations/useBaseTranslation';
import { useDefaultTranslation } from 'articleEditor/hooks/useDefaultTranslation';
import { RetranslationWarningModal } from 'articleEditor/modals/retranslationWarningModal';
import { useModal } from 'lib-knowledge-modal';
import { TranslatingType } from 'lib-knowledge-remote-types/general.types';
import { useContext, useMemo } from 'react';
import { snapshot } from 'valtio';

export const useTranslateTitle = () => {
  const { savingStore } = useContext(ArticleSavingContext);
  const {
    currentArticleDetails: { titleTranslatedAt, titleUpdatedAt },
  } = snapshot(savingStore);
  const defaultTranslation = useDefaultTranslation();
  const parsedTitleUpdatedAt = useMemo(() => {
    if (titleUpdatedAt) {
      return Date.parse(titleUpdatedAt);
    }
    return undefined;
  }, [titleUpdatedAt]);
  const parsedTitleTranslatedAt = useMemo(() => {
    if (titleTranslatedAt) {
      return Date.parse(titleTranslatedAt);
    }
    return undefined;
  }, [titleTranslatedAt]);
  const { showModal } = useModal();
  const {
    translate,
    stopLoadingAndShowErrorNotification,
    setGlobalLoading,
    clearTitle,
  } = useBaseTranslation({
    translatingType: TranslatingType.TITLE,
  });

  const translateTitle = async () => {
    if (!defaultTranslation?.draft) {
      stopLoadingAndShowErrorNotification();
      return;
    }
    setGlobalLoading(true);
    try {
      await clearTitle();
      await translate({
        title: defaultTranslation.draft.title,
      });
    } catch {
      stopLoadingAndShowErrorNotification();
    }
  };

  return async (): Promise<void> => {
    if (
      parsedTitleUpdatedAt &&
      parsedTitleTranslatedAt &&
      parsedTitleUpdatedAt > parsedTitleTranslatedAt
    ) {
      showModal(RetranslationWarningModal, {
        title: 'Overwrite title translation?',
        body: `The title translation of this article has been modified after it was translated. Are you sure you want to retranslate it? This action will overwrite the title translation and is NOT reversible.`,
        onAccept: () => translateTitle(),
      });
    } else {
      await translateTitle();
    }
  };
};
